import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Optional,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, merge, Observable, of, ReplaySubject, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { ICONS } from '@nimbus-library/features/shared/resources/icons';
import {
  IChatFormField,
  IChatForm,
  IChatFormResolvedEvent,
  ChatFormFieldType,
  ChatFormOperation,
} from '@nimbus-global/features/chat-form/models/chat-form.model';
import { IChatFormFieldAction, IChatFormResolver } from '@nimbus-global/features/chat-form/models/chat-form-resolver.model';
import { CompanionReferenceType } from '@nimbus-library/features/companion-chat/models/companion-conversation.model';
import { IOptionItem } from '@nimbus-library/features/shared/models/interfaces/i-option-item';

@UntilDestroy()
@Component({
  selector: 'nimbus-chat-message-form',
  standalone: false,
  templateUrl: './chat-message-form.component.html',
  styleUrl: './chat-message-form.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageFormComponent implements OnInit, OnChanges {
  @Input()
  public chatForm: IChatForm;
  @Input()
  public formResolver: IChatFormResolver;

  @Output()
  public resolved = new EventEmitter<IChatFormResolvedEvent>();
  @Output()
  public formActionInvoked = new EventEmitter<IChatFormFieldAction>();

  private readonly fieldRequests: Record<string, Subject<void>> = {};
  private readonly fieldLoading = new Set<string>();
  private readonly fieldContexts: Record<string, Record<string, string>> = {};
  private readonly formRebuilt$ = new Subject<void>();

  public readonly commentMaxLength = 1000;
  public readonly comment = new UntypedFormControl('', Validators.maxLength(this.commentMaxLength));

  public readonly TranslationKeys = TranslationKeys;
  public readonly ICONS = ICONS;
  public readonly ChatFormFieldType = ChatFormFieldType;
  public readonly ChatFormOperation = ChatFormOperation;
  public readonly CompanionReferenceType = CompanionReferenceType;

  public form: UntypedFormGroup;
  public optionsByField: Record<string, ReplaySubject<IOptionItem[]>> = {};
  public readonly loading$ = new BehaviorSubject<boolean>(false);
  public fieldValues$: Record<string, Observable<string>> = {};

  public constructor(@Optional() private readonly changeDetectorRef: ChangeDetectorRef = null) {}

  public getFieldAction(field: IChatFormField): IChatFormFieldAction | null {
    if (!this.formResolver?.resolveFieldAction) {
      return null;
    }
    const currentValues = this.getCurrentValues();
    return this.formResolver.resolveFieldAction(this.chatForm.entity, field.name, currentValues);
  }

  public getFieldMaxLength(field: IChatFormField): number | null {
    return this.formResolver?.resolveFieldMaxLength?.(this.chatForm.entity, field.name) ?? null;
  }

  public isFieldVisible(field: IChatFormField): boolean {
    return field.type !== ChatFormFieldType.Hidden;
  }

  public isSummaryFieldVisible(field: IChatFormField): boolean {
    const value = this.chatForm?.values?.[field.name];
    return this.isFieldVisible(field) && value != null && value !== '';
  }

  public onFieldContextChanged(fieldName: string, context: Record<string, string>): void {
    this.fieldContexts[fieldName] = context;
    this.changeDetectorRef?.markForCheck();
  }

  public ngOnInit(): void {
    this.buildForm();
    this.bindFormState();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (!this.form) {
      return;
    }
    if (changes.chatForm || changes.formResolver) {
      const prev = changes.chatForm?.previousValue as IChatForm;
      const curr = changes.chatForm?.currentValue as IChatForm;
      if (changes.formResolver || prev?.fields !== curr?.fields) {
        this.buildForm();
      }
      this.bindFormState();
    }
  }

  public get formTitle(): TranslationKeys | null {
    return this.formResolver?.resolveTitle?.(this.chatForm?.entity, this.chatForm?.operation) ?? null;
  }

  public get formDescription(): TranslationKeys | null {
    return this.formResolver?.resolveDescription?.(this.chatForm?.entity, this.chatForm?.operation) ?? null;
  }

  public get isResolved(): boolean {
    return this.chatForm?.values !== undefined;
  }

  public get isSubmitting(): boolean {
    return !!this.chatForm?.isSubmitting;
  }

  public get submitLabel(): TranslationKeys {
    return this.chatForm?.operation === ChatFormOperation.Confirm ? TranslationKeys.GeneralApprove : TranslationKeys.ChatWidgetFormSubmit;
  }

  public onSubmit(): void {
    if (this.form.invalid || this.comment.invalid || this.isResolved || this.isSubmitting) {
      return;
    }
    this.resolved.emit({ values: this.form.getRawValue(), comment: this.getComment() });
  }

  public onReject(): void {
    if (this.comment.invalid || this.isResolved || this.isSubmitting) {
      return;
    }
    this.resolved.emit({ comment: this.getComment() });
  }

  private getComment(): string | undefined {
    return (this.comment.value as string | null)?.trim() || undefined;
  }

  private buildForm(): void {
    this.formRebuilt$.next();
    const controls: Record<string, UntypedFormControl> = {};
    this.fieldValues$ = {};
    for (const name of Object.keys(this.fieldContexts)) {
      delete this.fieldContexts[name];
    }
    const fields = this.chatForm?.fields ?? [];

    for (const field of fields) {
      const maxLength = this.getFieldMaxLength(field);
      const validators = [...(field.required ? [Validators.required] : []), ...(maxLength ? [Validators.maxLength(maxLength)] : [])];
      controls[field.name] = new UntypedFormControl(field.valueDefault ?? null, validators);
    }

    this.form = new UntypedFormGroup(controls);
    this.restoreFormValues();

    for (const field of fields) {
      if (this.canResolveReferenceOptions(field)) {
        this.loadFormFieldReferenceOptions(field);
      }
      this.fieldValues$[field.name] = this.buildFieldValue$(field);
    }

    this.bindCascadeTriggers();
    this.setPendingValues();
    this.bindFormValueUpdate();
  }

  private restoreFormValues(): void {
    if (!this.isResolved) {
      this.form.patchValue(this.chatForm?.valuesPending ?? {}, { emitEvent: false });
    }
    this.comment.setValue((this.isResolved ? this.chatForm.comment : this.chatForm?.commentPending) ?? '', { emitEvent: false });
  }

  private setPendingValues(): void {
    merge(this.form.valueChanges, this.comment.valueChanges)
      .pipe(takeUntil(this.formRebuilt$), untilDestroyed(this))
      .subscribe(() => {
        if (!this.chatForm || this.isResolved || this.isSubmitting) {
          return;
        }
        this.chatForm.valuesPending = this.form.getRawValue();
        this.chatForm.commentPending = this.comment.value as string;
      });
  }

  private canResolveReferenceOptions(field: IChatFormField): boolean {
    return (
      (field.type === ChatFormFieldType.Reference || (field.type === ChatFormFieldType.Control && this.isResolved)) &&
      !!field.referenceType &&
      !!this.formResolver?.resolveReferenceOptions
    );
  }

  private buildFieldValue$(field: IChatFormField): Observable<string> {
    const value = this.chatForm?.values?.[field.name];
    if (value == null || value === '') {
      return of('');
    }
    if (field.type === ChatFormFieldType.Reference || field.type === ChatFormFieldType.Control) {
      const subject = this.optionsByField[field.name];
      if (subject) {
        return subject.pipe(map((options) => options.find((o) => o.id === value)?.name ?? String(value)));
      }
    }
    return of(String(value));
  }

  private bindFormValueUpdate(): void {
    if (!this.formResolver?.observeFormValueUpdates || !this.chatForm) {
      return;
    }
    this.formResolver
      .observeFormValueUpdates(this.chatForm.entity)
      .pipe(untilDestroyed(this))
      .subscribe((update) => {
        if (this.isResolved || this.isSubmitting) {
          return;
        }
        const currentValues = this.form.getRawValue() as Record<string, unknown>;
        const matches = !update.requiresValues || Object.entries(update.requiresValues).every(([k, v]) => currentValues[k] === v);
        if (matches) {
          this.form.patchValue(update.values);
        }
      });
  }

  private bindCascadeTriggers(): void {
    const triggers = (this.chatForm?.fields ?? []).filter((field) => field.isCascade);
    for (const trigger of triggers) {
      this.form
        .get(trigger.name)
        .valueChanges.pipe(untilDestroyed(this))
        .subscribe(() => this.onCascadeTriggerChanged(trigger.name));
    }
  }

  private onCascadeTriggerChanged(triggerName: string): void {
    const dependents = (this.chatForm?.fields ?? [])
      .filter((field) => field.type === ChatFormFieldType.Reference && field.referenceType)
      .filter((field) => field.name !== triggerName);
    for (const dependent of dependents) {
      this.form.get(dependent.name)?.reset(null, { emitEvent: false });
    }
    for (const dependent of dependents) {
      this.loadFormFieldReferenceOptions(dependent);
    }
  }

  private loadFormFieldReferenceOptions(field: IChatFormField): void {
    const existingOptions$ = this.optionsByField[field.name];
    const options$ = existingOptions$ ?? new ReplaySubject<IOptionItem[]>(1);
    this.optionsByField[field.name] = options$;

    let request$ = this.fieldRequests[field.name];
    if (!request$) {
      request$ = new Subject<void>();
      this.fieldRequests[field.name] = request$;
      request$
        .pipe(
          switchMap(() =>
            this.formResolver
              .resolveReferenceOptions(field.referenceType, this.chatForm, this.getCurrentValues())
              .pipe(catchError(() => of<IOptionItem[]>([]))),
          ),
          untilDestroyed(this),
        )
        .subscribe((options) => {
          options$.next(options);
          this.setFormLoading(field.name, false);
        });
    }
    if (existingOptions$) {
      options$.next([]);
    }
    this.setFormLoading(field.name, true);
    request$.next();
  }

  private setFormLoading(name: string, loading: boolean): void {
    if (loading) {
      this.fieldLoading.add(name);
    } else {
      this.fieldLoading.delete(name);
    }

    this.loading$.next(this.fieldLoading.size > 0);
  }

  private getCurrentValues(): Record<string, unknown> {
    return Object.assign({}, ...Object.values(this.fieldContexts), this.form?.getRawValue(), this.chatForm?.values);
  }

  private bindFormState(): void {
    if (!this.form || !this.chatForm) {
      return;
    }

    if (this.isResolved || this.isSubmitting) {
      this.form.disable({ emitEvent: false });
    } else {
      this.form.enable({ emitEvent: false });
    }

    if (!this.isResolved) {
      return;
    }

    this.form.patchValue(this.chatForm.values ?? {}, { emitEvent: false });
    for (const field of this.chatForm.fields) {
      if (this.canResolveReferenceOptions(field) && !this.optionsByField[field.name]) {
        this.loadFormFieldReferenceOptions(field);
      }
      this.fieldValues$[field.name] = this.buildFieldValue$(field);
    }
  }
}
